import React from 'react';
import styled from '@emotion/styled';
import { Button } from '../Button/Button';
import { CrossIcon } from '../Icons/CrossIcon';

import { config } from '../../config';
import NewsletterCreditOffer from './NewsletterCreditOffer';
import { __ } from '../../helpers/TranslationService';
import { eventTracker } from '../../helpers/EventTracker/EventTracker';

type NewsletterInitialScreenProps = {
    onBannerCloseHandler: () => void;
    onSignUpHandler: () => void;
    isGoogleSSOEnabled?: boolean;
    isAppleSSOEnabled?: boolean;
    isNetIDSSOEnabled?: boolean;
    isHotmailSSOEnabled?: boolean;
    currency: string;
    obcCredit?: number;
};

const Header = styled.div`
    padding: 16px;
    display: flex;
    flex-direction: row;
    align-items: center;
    border-bottom: 1px solid ${(props) => props.theme.border.onBg};

    img {
        max-width: 16px;
        margin-right: 8px;

        &:last-child {
            margin-right: 0;
        }
    }

    *:last-child {
        margin-left: auto;
    }
`;

const Body = styled.div`
    background: ${(props) => props.theme.background.page};
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 16px;

    img,
    svg {
        margin-left: 16px;
        max-width: 130px;
        flex: 1 0 auto;
    }

    svg text {
        transform: rotate(7deg);
        text-shadow: 0 0.563px 2.25px rgba(0, 0, 0, 0.35);
    }
`;

const Footer = styled.div`
    background: ${(props) => props.theme.background.page};
    padding: 16px;
    padding-top: 0;
`;

const HeaderTitle = styled.p`
    color: #000;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 22px; /* 157.143% */
    margin: 0 8px;
`;

const Title = styled.p`
    color: #000;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
    margin: 0;
`;

const Description = styled.p`
    color: ${(props) => props.theme.text.color.primary2};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    margin-bottom: 0;
`;

export const NewsletterInitialScreen: React.FC<NewsletterInitialScreenProps> = ({
    onBannerCloseHandler,
    isGoogleSSOEnabled,
    isAppleSSOEnabled,
    isHotmailSSOEnabled,
    isNetIDSSOEnabled,
    onSignUpHandler,
    currency,
    obcCredit,
}) => {
    React.useEffect(() => {
        eventTracker.trackG4Event('newsletter', {
            step: 'start',
        });
    }, []);

    return (
        <div>
            <Header>
                {isHotmailSSOEnabled && (
                    <img src={`${config.images}/common/outlook.svg`} alt="Hotmail SSO" />
                )}
                {isGoogleSSOEnabled && (
                    <img src={`${config.images}/common/google-sso.svg`} alt="Google SSO" />
                )}
                {isAppleSSOEnabled && (
                    <img src={`${config.images}/common/apple-sso.svg`} alt="Apple SSO" />
                )}
                {isNetIDSSOEnabled && (
                    <img src={`${config.images}/common/gmx-sso.svg`} alt="GMX SSO" />
                )}

                <HeaderTitle> {__('newsletter.bannerHeadTitle', 'dreamlines')} </HeaderTitle>

                <Button
                    paddings={false}
                    rounded={false}
                    color="flatSecondary"
                    size="small"
                    onClick={onBannerCloseHandler}
                >
                    <CrossIcon width={16} height={16} />
                </Button>
            </Header>

            <Body>
                <div>
                    <Title>{__('newsletter.headline', 'dreamlines')}</Title>
                    <Description>{__('newsletter.subheadline', 'dreamlines')}</Description>
                </div>

                {obcCredit && <NewsletterCreditOffer currency={currency} amount={obcCredit} />}

                {!obcCredit && (
                    <img alt="Newsletter" src={`${config.images}/common/newsletter.svg`} />
                )}
            </Body>
            <Footer>
                <Button fullWidth onClick={onSignUpHandler} size="large" color="primary">
                    {__('general.signUp', 'dreamlines')}
                </Button>
            </Footer>
        </div>
    );
};
